import { useMemo } from 'react';
import { useTenantStore } from '../../shared/stores/useTenantStore';
import { usePermissions, Permission } from '../../shared/hooks/usePermissions';
import { useMerchantSettlements } from '../settlements/hooks/useSettlements';
import { MerchantPayoutDestination } from '../payout-destinations/MerchantPayoutDestination';
import { EarningsSummary } from './EarningsSummary';
import { SettlementHistoryTable } from './SettlementHistoryTable';
import { computeEarnings } from './earnings';

export default function Payouts() {
  const activeMerchant = useTenantStore((s) => s.activeMerchant);
  const { hasPermission } = usePermissions();

  if (!activeMerchant) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <p className="text-sm text-[#3F3E42]">Select a merchant to view payouts.</p>
      </div>
    );
  }

  if (!hasPermission(Permission.SETTLEMENT_VIEW)) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <p className="text-sm text-[#3F3E42]">You don&apos;t have permission to view payouts.</p>
      </div>
    );
  }

  return <PayoutsInner tenantId={activeMerchant.id} businessName={activeMerchant.name} />;
}

interface InnerProps {
  tenantId: string;
  businessName?: string;
}

function PayoutsInner({ tenantId, businessName }: InnerProps) {
  const { data: settlements = [], isLoading, error } = useMerchantSettlements(tenantId);

  const summary = useMemo(() => computeEarnings(settlements), [settlements]);

  return (
    <div className="space-y-6">
      <h1 className="text-xl font-semibold text-[#28282A]">Payouts</h1>

      {isLoading && <p className="text-sm text-[#3F3E42]">Loading payouts…</p>}
      {error && <p className="text-sm text-red-500">Failed to load payouts.</p>}

      {!isLoading && !error && (
        <>
          <EarningsSummary summary={summary} />
          <MerchantPayoutDestination tenantId={tenantId} businessName={businessName} maskAccount />
          <SettlementHistoryTable settlements={settlements} />
        </>
      )}
    </div>
  );
}
